import {
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useMemo,
} from 'react';

import { Skeleton } from '@chakra-ui/react';
import type { UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

interface QueryWrapperProps {
  queries: Array<UseQueryResult | Falsy>;
  LoadingComponent?: ReactElement;
  debug_isLoading?: boolean;
}

export const useQueryWrapper = (queries: Array<UseQueryResult | Falsy>) => {
  const unifiedQueries = useMemo(
    () => queries.filter((x) => !!x) as Array<UseQueryResult>,
    [queries]
  );

  // Check if any query is in loading state
  const isLoading = useMemo(
    () => unifiedQueries.some((x) => x.isLoading),
    [unifiedQueries]
  );

  // Check if any query has an error or is paused due to lack of internet connection
  const hasError = useMemo(() => {
    return unifiedQueries.some((x) => x.isError || (x.isLoading && x.isPaused));
  }, [unifiedQueries]);

  // 404 error
  const hasNotFoundError = useMemo(() => {
    return unifiedQueries.some((x) => {
      return (
        x.isError && isAxiosError(x.error) && x.error?.response?.status === 404
      );
    });
  }, [unifiedQueries]);

  // Refetch function to retrigger queries that have errors or are paused
  const refetch = useCallback(() => {
    unifiedQueries.forEach((query) => {
      if (query.isError || (query.isLoading && query.isPaused)) {
        query.refetch();
      }
    });
  }, [unifiedQueries]);

  return {
    isLoading,
    hasError,
    refetch,
    hasNotFoundError,
  };
};

export const QueryWrapper: React.FC<PropsWithChildren<QueryWrapperProps>> = ({
  children,
  queries,
  LoadingComponent,
  debug_isLoading,
}) => {
  const { isLoading } = useQueryWrapper(queries);

  // If loading, display a loading spinner
  if (isLoading || debug_isLoading) {
    if (LoadingComponent) return LoadingComponent;
    else return <Skeleton h="full" />;
  }

  // Default render: the child components
  return children;
};
